import React, { useState, useEffect, useCallback } from 'react';
import { Card, DataTable, Link, Badge, Text, BlockStack, InlineStack, Spinner, Tabs, Pagination, Select, TextField, Button } from '@shopify/polaris';
import { formatToCurrency, formatDate } from '../../../shared/components/functions';

function SaleOrderLinesTable({ FIRNR }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('50');
  const [productNameFilter, setProductNameFilter] = useState('');
  const [productIdFilter, setProductIdFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ productName: '', productId: '' });

  const fetchData = useCallback(async () => {
    if (!FIRNR) return;
    setLoading(true);
    try {
      const limit = parseInt(pageSize);
      const offset = (currentPage - 1) * limit;

      const params = new URLSearchParams({
        companyId: FIRNR,
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (appliedFilters.productName) {
        params.append('productName', appliedFilters.productName);
      }
      if (appliedFilters.productId) {
        params.append('productId', appliedFilters.productId);
      }

      // Fetch count
      const countParams = new URLSearchParams({ companyId: FIRNR });
      if (appliedFilters.productName) countParams.append('productName', appliedFilters.productName);
      if (appliedFilters.productId) countParams.append('productId', appliedFilters.productId);

      const countResponse = await fetch(`/api/metrics/company-sale-lines-count?${countParams}`);
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalCount(countData.count || 0);
      }

      // Fetch page data
      const response = await fetch(`/api/metrics/company-sale-lines?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLines(Array.isArray(data) ? data : []);
      } else {
        setLines([]);
      }
    } catch (err) {
      console.error('Error fetching sale lines:', err);
      setLines([]);
    }
    setLoading(false);
  }, [FIRNR, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedFilters({ productName: productNameFilter, productId: productIdFilter });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setProductNameFilter('');
    setProductIdFilter('');
    setAppliedFilters({ productName: '', productId: '' });
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const totalPages = Math.ceil(totalCount / parseInt(pageSize));

  const rows = lines.map((item) => [
    <Link removeUnderline url={`/sales/${item.OrderbevestigingID}`}>
      {item.Orderbevestigingnummer}
    </Link>,
    item.Datum ? formatDate(item.Datum) : '-',
    item.ProductID ? (
      <Link removeUnderline url={`/products/${item.ProductID}`}>
        {item.ProductID}
      </Link>
    ) : (
      '-'
    ),
    <Text variant="bodyMd" as="span" truncate>
      {item.ProductName || item.tekstlijn || '-'}
    </Text>,
    item.aantal ? Number(item.aantal).toFixed(2) : '-',
    item.VP ? formatToCurrency(item.VP) : '-',
    item.line_total ? formatToCurrency(item.line_total) : '-',
    item.RefKlant || '-'
  ]);

  const pageSizeOptions = [
    { label: '25 per page', value: '25' },
    { label: '50 per page', value: '50' },
    { label: '100 per page', value: '100' },
    { label: '250 per page', value: '250' }
  ];

  const hasActiveFilters = appliedFilters.productName || appliedFilters.productId;

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" wrap={false}>
        <div>
          <Text variant="headingMd" as="h3">
            Sale Order Lines
          </Text>
          <Text tone="subdued" as="p">
            Products sold to this customer (chronological)
          </Text>
        </div>
        <InlineStack gap="300" align="center">
          <Select
            label="Page size"
            labelHidden
            options={pageSizeOptions}
            value={pageSize}
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          />
          <Badge tone="info">{totalCount} lines</Badge>
        </InlineStack>
      </InlineStack>

      {/* Filters */}
      <div onKeyDown={handleKeyDown}>
        <InlineStack gap="300" align="start" blockAlign="end">
          <div style={{ width: '200px' }}>
            <TextField
              label="Product name"
              value={productNameFilter}
              onChange={setProductNameFilter}
              placeholder="Search product name..."
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setProductNameFilter('')}
            />
          </div>
          <div style={{ width: '150px' }}>
            <TextField
              label="Product ID"
              value={productIdFilter}
              onChange={setProductIdFilter}
              placeholder="e.g. 1234"
              autoComplete="off"
              type="number"
              clearButton
              onClearButtonClick={() => setProductIdFilter('')}
            />
          </div>
          <div style={{ paddingTop: '24px' }}>
            <InlineStack gap="200">
              <Button onClick={handleApplyFilters} variant="primary">
                Apply filters
              </Button>
              {hasActiveFilters && (
                <Button onClick={handleClearFilters} variant="plain">
                  Clear
                </Button>
              )}
            </InlineStack>
          </div>
        </InlineStack>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="large" />
        </div>
      ) : lines.length > 0 ? (
        <>
          <DataTable
            verticalAlign="middle"
            stickyHeader={true}
            increasedTableDensity={true}
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text']}
            headings={[<b>Sale #</b>, <b>Date</b>, <b>Product ID</b>, <b>Product Name</b>, <b>Qty</b>, <b>Unit Price</b>, <b>Line Total</b>, <b>Customer Ref</b>]}
            rows={rows}
          />
          {totalPages > 1 && (
            <InlineStack align="center" gap="400">
              <Pagination hasPrevious={currentPage > 1} onPrevious={() => setCurrentPage(currentPage - 1)} hasNext={currentPage < totalPages} onNext={() => setCurrentPage(currentPage + 1)} />
              <Text as="span" tone="subdued">
                Page {currentPage} of {totalPages}
              </Text>
            </InlineStack>
          )}
        </>
      ) : (
        <Text as="p" tone="subdued">
          No sale order lines found for this customer.
        </Text>
      )}
    </BlockStack>
  );
}

function InvoiceLinesTable({ FIRNR }) {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('50');
  const [productNameFilter, setProductNameFilter] = useState('');
  const [productIdFilter, setProductIdFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ productName: '', productId: '' });

  const fetchData = useCallback(async () => {
    if (!FIRNR) return;
    setLoading(true);
    try {
      const limit = parseInt(pageSize);
      const offset = (currentPage - 1) * limit;

      const params = new URLSearchParams({
        companyId: FIRNR,
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (appliedFilters.productName) {
        params.append('productName', appliedFilters.productName);
      }
      if (appliedFilters.productId) {
        params.append('productId', appliedFilters.productId);
      }

      // Fetch count
      const countParams = new URLSearchParams({ companyId: FIRNR });
      if (appliedFilters.productName) countParams.append('productName', appliedFilters.productName);
      if (appliedFilters.productId) countParams.append('productId', appliedFilters.productId);

      const countResponse = await fetch(`/api/metrics/company-invoice-line-details-count?${countParams}`);
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalCount(countData.count || 0);
      }

      // Fetch page data
      const response = await fetch(`/api/metrics/company-invoice-line-details?${params}`);
      if (response.ok) {
        const data = await response.json();
        setLines(Array.isArray(data) ? data : []);
      } else {
        setLines([]);
      }
    } catch (err) {
      console.error('Error fetching invoice lines:', err);
      setLines([]);
    }
    setLoading(false);
  }, [FIRNR, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyFilters = () => {
    setAppliedFilters({ productName: productNameFilter, productId: productIdFilter });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setProductNameFilter('');
    setProductIdFilter('');
    setAppliedFilters({ productName: '', productId: '' });
    setCurrentPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const totalPages = Math.ceil(totalCount / parseInt(pageSize));

  const getStatusBadge = (status, statusText) => {
    const text = statusText || status;
    if (status === 0) {
      // Open
      return <Badge size="small">{text}</Badge>;
    } else if (status === 9) {
      // Paid
      return (
        <Badge tone="success" size="small">
          {text}
        </Badge>
      );
    } else if (status === 1) {
      // 1st reminder
      return (
        <Badge tone="attention" size="small">
          {text}
        </Badge>
      );
    } else if (status === 2) {
      // 2nd reminder
      return (
        <Badge tone="warning" size="small">
          {text}
        </Badge>
      );
    } else {
      // Failure and others
      return (
        <Badge tone="critical" size="small">
          {text}
        </Badge>
      );
    }
  };

  const rows = lines.map((item) => [
    <Link removeUnderline url={`/invoices/${item.faktuurnr}`}>
      {item.Factuurnummer}
    </Link>,
    item.datum ? formatDate(item.datum) : '-',
    item.ProductID ? (
      <Link removeUnderline url={`/products/${item.ProductID}`}>
        {item.ProductID}
      </Link>
    ) : (
      '-'
    ),
    <Text variant="bodyMd" as="span" truncate>
      {item.ProductName || item.tekstlijn || '-'}
    </Text>,
    item.aantal ? Number(item.aantal).toFixed(2) : '-',
    item.VP ? formatToCurrency(item.VP) : '-',
    item.line_total ? formatToCurrency(item.line_total) : '-',
    getStatusBadge(item.status, item.statusText),
    item.refklant || '-'
  ]);

  const pageSizeOptions = [
    { label: '25 per page', value: '25' },
    { label: '50 per page', value: '50' },
    { label: '100 per page', value: '100' },
    { label: '250 per page', value: '250' }
  ];

  const hasActiveFilters = appliedFilters.productName || appliedFilters.productId;

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" wrap={false}>
        <div>
          <Text variant="headingMd" as="h3">
            Invoice Lines
          </Text>
          <Text tone="subdued" as="p">
            Products invoiced to this customer (chronological)
          </Text>
        </div>
        <InlineStack gap="300" align="center">
          <Select
            label="Page size"
            labelHidden
            options={pageSizeOptions}
            value={pageSize}
            onChange={(value) => {
              setPageSize(value);
              setCurrentPage(1);
            }}
          />
          <Badge tone="info">{totalCount} lines</Badge>
        </InlineStack>
      </InlineStack>

      {/* Filters */}
      <div onKeyDown={handleKeyDown}>
        <InlineStack gap="300" align="start" blockAlign="end">
          <div style={{ width: '200px' }}>
            <TextField
              label="Product name"
              value={productNameFilter}
              onChange={setProductNameFilter}
              placeholder="Search product name..."
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setProductNameFilter('')}
            />
          </div>
          <div style={{ width: '150px' }}>
            <TextField
              label="Product ID"
              value={productIdFilter}
              onChange={setProductIdFilter}
              placeholder="e.g. 1234"
              autoComplete="off"
              type="number"
              clearButton
              onClearButtonClick={() => setProductIdFilter('')}
            />
          </div>
          <div style={{ paddingTop: '24px' }}>
            <InlineStack gap="200">
              <Button onClick={handleApplyFilters} variant="primary">
                Apply filters
              </Button>
              {hasActiveFilters && (
                <Button onClick={handleClearFilters} variant="plain">
                  Clear
                </Button>
              )}
            </InlineStack>
          </div>
        </InlineStack>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="large" />
        </div>
      ) : lines.length > 0 ? (
        <>
          <DataTable
            verticalAlign="middle"
            stickyHeader={true}
            increasedTableDensity={true}
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
            headings={[<b>Invoice #</b>, <b>Date</b>, <b>Product ID</b>, <b>Product Name</b>, <b>Qty</b>, <b>Unit Price</b>, <b>Line Total</b>, <b>Status</b>, <b>Customer Ref</b>]}
            rows={rows}
          />
          {totalPages > 1 && (
            <InlineStack align="center" gap="400">
              <Pagination hasPrevious={currentPage > 1} onPrevious={() => setCurrentPage(currentPage - 1)} hasNext={currentPage < totalPages} onNext={() => setCurrentPage(currentPage + 1)} />
              <Text as="span" tone="subdued">
                Page {currentPage} of {totalPages}
              </Text>
            </InlineStack>
          )}
        </>
      ) : (
        <Text as="p" tone="subdued">
          No invoice lines found for this customer.
        </Text>
      )}
    </BlockStack>
  );
}

export default function CompanySalesHistory({ FIRNR }) {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = [
    {
      id: 'sale-lines',
      content: 'Sale Order Lines',
      panelID: 'sale-lines-panel'
    },
    {
      id: 'invoice-lines',
      content: 'Invoice Lines',
      panelID: 'invoice-lines-panel'
    }
  ];

  const handleTabChange = useCallback((selectedTabIndex) => {
    setSelectedTab(selectedTabIndex);
  }, []);

  return (
    <Card>
      <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} fitted>
        <div style={{ paddingTop: '16px' }}>
          {selectedTab === 0 && <SaleOrderLinesTable FIRNR={FIRNR} />}
          {selectedTab === 1 && <InvoiceLinesTable FIRNR={FIRNR} />}
        </div>
      </Tabs>
    </Card>
  );
}
